import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { filter } from 'rxjs/operators';
import { Answer } from '../models/questionnaire.model';

export type AnswersMap = { [slug: string]: Answer | undefined };

/**
 * Holds the in-memory snapshot of the current questionnaire's answers.
 *
 * Provided per-questionnaire (NOT root) — see QuestionnaireAnswerComponent providers.
 * The smart component pushes updates here on every `onChangeAnswer`; downstream
 * components (notably QuestionApiComponent) subscribe to react in real time without
 * waiting for the debounced DB save.
 */
@Injectable()
export class QuestionnaireAnswersStateService {
  private readonly state$ = new BehaviorSubject<AnswersMap>({});
  private readonly blur$ = new Subject<string>();
  private questionnaireId: string | null = null;

  /** Stream of full answers map — emits on every patch. */
  changes$(): Observable<AnswersMap> {
    return this.state$.asObservable();
  }

  /** Current snapshot. Use this for one-shot reads (e.g. building a request payload). */
  getSnapshot(): AnswersMap {
    return this.state$.value;
  }

  /**
   * Replaces the full state — used when loading an existing questionnaire answer
   * AND on every change emission. The inner `question.answer` objects are kept
   * by reference (ngModel mutates them in place); subscribers that need a stable
   * comparison should derive a primitive key at emission time instead of relying
   * on object identity.
   */
  setAll(answers: AnswersMap): void {
    this.state$.next({ ...(answers ?? {}) });
  }

  /** Patches a single slug. */
  patch(slug: string, answer: Answer | undefined): void {
    this.state$.next({ ...this.state$.value, [slug]: answer });
  }

  /** Clears the state. Called on navigation away from a questionnaire. */
  clear(): void {
    this.state$.next({});
  }

  /** Stores the questionnaire ID so child components (QuestionApiComponent) can read it. */
  setQuestionnaireId(id: string): void {
    this.questionnaireId = id;
  }

  getQuestionnaireId(): string | null {
    return this.questionnaireId;
  }

  /** Signal that the input for a given slug lost focus. */
  blurSlug(slug: string): void {
    this.blur$.next(slug);
  }

  /** Stream of blur events filtered to the given slugs. */
  slugBlurs$(slugs: string[]): Observable<string> {
    return this.blur$.asObservable().pipe(filter((s) => slugs.includes(s)));
  }
}
